import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Badge, Button, Card, CardBody, CardHeader, ErrorState, Field, LoadingState, Select, TextArea, TextInput } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import type { AdminApplicationSummary, AdminBundleDetail, AdminReference, CatalogLifecycleStatus } from "@odingard/sdk-client";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

const LIFECYCLE_STATUSES: CatalogLifecycleStatus[] = [
  "draft", "internal", "preview", "beta", "available", "maintenance", "suspended", "deprecated", "retired", "coming_soon",
];

function useSaver() {
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      await fn();
      setNotice(`${label} saved.`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `Failed to save ${label}.`);
    } finally {
      setBusy(false);
    }
  }
  return { notice, error, busy, run };
}

function SaveFooter({ saver }: { saver: ReturnType<typeof useSaver> }) {
  return (
    <>
      {saver.notice && <p className="ods-help" role="status">{saver.notice}</p>}
      {saver.error && <p className="ods-error-text" role="alert">{saver.error}</p>}
    </>
  );
}

export function AdminBundle() {
  const { slug = "" } = useParams();
  const detail = useAsync(async () => api.adminBundle(slug), [slug]);
  const apps = useAsync(async () => (await api.adminApplications()).applications, []);
  const reference = useAsync(async () => api.adminReference(), []);

  if (detail.loading || apps.loading || reference.loading) return <LoadingState label="Loading bundle…" />;
  if (detail.error) return <ErrorState description={detail.error.message} onRetry={detail.reload} />;
  if (!detail.data) return <ErrorState description="Bundle not found." />;

  return <BundleForm data={detail.data} allApps={apps.data ?? []} reference={reference.data} onChanged={detail.reload} />;
}

function BundleForm({ data, allApps, reference, onChanged }: { data: AdminBundleDetail; allApps: AdminApplicationSummary[]; reference: AdminReference | null; onChanged: () => void }) {
  const slug = data.bundle.slug;
  const details = useSaver();
  const lifecycle = useSaver();
  const membership = useSaver();
  const pricing = useSaver();

  const [name, setName] = useState(data.bundle.name);
  const [description, setDescription] = useState(data.bundle.description);
  const [primaryEntitlement, setPrimaryEntitlement] = useState(data.bundle.primaryEntitlement);
  const [planKey, setPlanKey] = useState(data.bundle.planKey ?? "");
  const [sortOrder, setSortOrder] = useState(String(data.bundle.sortOrder));
  const [status, setStatus] = useState<CatalogLifecycleStatus>(data.bundle.lifecycleStatus);
  const [members, setMembers] = useState<string[]>(data.applications.map((a) => a.slug));
  const [prices, setPrices] = useState(data.prices.map((p) => ({ priceId: p.priceId, interval: p.interval })));

  function toggleMember(appSlug: string) {
    setMembers((prev) => (prev.includes(appSlug) ? prev.filter((s) => s !== appSlug) : [...prev, appSlug]));
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{data.bundle.name}</h1>
          <p><code>{slug}</code> · <Badge tone="neutral">{data.bundle.lifecycleStatus}</Badge></p>
        </div>
        <Link to="/studio/catalog-admin"><Button size="sm" variant="ghost">Back</Button></Link>
      </div>

      <Card>
        <CardHeader title="Details" />
        <CardBody>
          <div className="ods-form-grid">
            <Field label="Name">{(id) => <TextInput id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
            <Field label="Primary entitlement">{(id) => <TextInput id={id} value={primaryEntitlement} onChange={(e) => setPrimaryEntitlement(e.target.value)} />}</Field>
            <Field label="Plan">{(id) => (
              <Select id={id} value={planKey} onChange={(e) => setPlanKey(e.target.value)}>
                <option value="">None</option>
                {(reference?.plans ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Select>
            )}</Field>
            <Field label="Sort order">{(id) => <TextInput id={id} type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />}</Field>
          </div>
          <Field label="Description">{(id) => <TextArea id={id} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />}</Field>
          <SaveFooter saver={details} />
          <Button size="sm" disabled={details.busy} onClick={() => details.run("Details", () => api.updateAdminBundle(slug, { name, description, primaryEntitlement, planKey: planKey || null, sortOrder: Number(sortOrder) || 0 }).then(onChanged))}>Save details</Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Lifecycle" />
        <CardBody>
          <Field label="Status" help={`Current: ${data.bundle.lifecycleStatus}`}>{(id) => (
            <Select id={id} value={status} onChange={(e) => setStatus(e.target.value as CatalogLifecycleStatus)}>
              {LIFECYCLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          )}</Field>
          <SaveFooter saver={lifecycle} />
          <Button size="sm" disabled={lifecycle.busy} onClick={() => lifecycle.run("Lifecycle", () => api.setAdminBundleLifecycle(slug, status).then(onChanged))}>Apply transition</Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Applications" />
        <CardBody>
          <p className="ods-help">Select the applications included in this bundle.</p>
          <div className="ods-checkbox-grid">
            {allApps.map((a) => (
              <label key={a.id} className="ods-row" style={{ gap: 8 }}>
                <input type="checkbox" checked={members.includes(a.slug)} onChange={() => toggleMember(a.slug)} />
                <span>{a.icon} {a.name}</span>
              </label>
            ))}
          </div>
          <SaveFooter saver={membership} />
          <Button size="sm" disabled={membership.busy} onClick={() => membership.run("Applications", () => api.setAdminBundleApplications(slug, members).then(onChanged))}>Save applications</Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Pricing references" action={<Button size="sm" variant="ghost" onClick={() => setPrices((p) => [...p, { priceId: "", interval: "month" }])}>Add</Button>} />
        <CardBody>
          <p className="ods-help">Pricing authority stays in the billing plans/prices. The catalog only references existing prices.</p>
          {prices.map((r, i) => (
            <div className="ods-form-grid" key={i}>
              <Field label="Price">{(id) => (
                <Select id={id} value={r.priceId} onChange={(e) => setPrices((prev) => prev.map((x, idx) => (idx === i ? { ...x, priceId: e.target.value } : x)))}>
                  <option value="">Select a price</option>
                  {(reference?.prices ?? []).map((p) => <option key={p.id} value={p.id}>{p.id} ({p.interval})</option>)}
                </Select>
              )}</Field>
              <Field label="Interval">{(id) => (
                <Select id={id} value={r.interval} onChange={(e) => setPrices((prev) => prev.map((x, idx) => (idx === i ? { ...x, interval: e.target.value } : x)))}>
                  <option value="month">month</option>
                  <option value="year">year</option>
                </Select>
              )}</Field>
              <div className="ods-field" style={{ justifyContent: "flex-end" }}>
                <Button size="sm" variant="ghost" onClick={() => setPrices((prev) => prev.filter((_, idx) => idx !== i))}>Remove</Button>
              </div>
            </div>
          ))}
          <SaveFooter saver={pricing} />
          <Button size="sm" disabled={pricing.busy} onClick={() => pricing.run("Prices", () => api.setAdminBundlePrices(slug, prices.filter((p) => p.priceId)).then(onChanged))}>Save prices</Button>
        </CardBody>
      </Card>
    </>
  );
}
